import { useQueryClient } from "@tanstack/react-query";
import { dataDogRemoveUser } from "PFApp/bootstrapper/use_datadog_rum";
import { useAppLayoutStoreActions } from "PFApp/stores/app_layout_store";
import { useAppShellStoreActions } from "PFApp/stores/app_shell_store";
import { useAuthStoreActions } from "PFApp/stores/auth_store";
import { useChatStoreActions } from "PFApp/stores/chat_store";
import { usePagesStoreActions } from "PFApp/stores/pages_store";
import api from "PFCore/api";
import { isMsTeams } from "PFCore/helpers/ms_teams";
import { accountKeys } from "PFCore/hooks/queries/account/query_keys";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import { updateMomentLocale } from "../bootstrapper/use_moment_locale";
import { changeLanguage } from "../use_change_language";

export type SignOutOptions = {
  redirectUri?: string;
  error?: string;
  skipRevoke?: boolean;
};

export const useSignOut = () => {
  const { clearTokens, getAccessToken } = useAuthStoreActions();
  const { reset: resetAppShellStore } = useAppShellStoreActions();
  const { reset: resetChatStore } = useChatStoreActions();
  const { reset: resetPagesStore } = usePagesStoreActions();
  const { reset: resetAppLayoutStore } = useAppLayoutStoreActions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: currentAccount } = useCurrentAccount();

  const navigateToLanding = useCallback(
    (options: SignOutOptions = {}) => {
      if (typeof sessionStorage !== "undefined" && sessionStorage !== null) {
        sessionStorage.setItem("signedOut", "true");
      }
      clearTokens();
      resetAppShellStore();
      resetChatStore();
      resetPagesStore();
      resetAppLayoutStore();
      // Clear password reset token for security
      window.storage.removeItem("reset_password_token");
      queryClient.clear();
      queryClient.setQueryData(accountKeys.basic(), currentAccount);
      dataDogRemoveUser();
      updateMomentLocale();
      changeLanguage(currentAccount.customization?.locale);

      let path = "/users/signin";

      if (options.redirectUri) {
        path = options.redirectUri;
      } else if (isMsTeams() || location.pathname.match(/ms_teams/)) {
        path = "/ms_teams/signin";
      } else if (options.error) {
        path = `${path}?error=${options.error}`;
      }
      navigate(path, { replace: !!currentAccount.slo_target_url });
    },
    [
      clearTokens,
      resetAppShellStore,
      resetChatStore,
      resetAppLayoutStore,
      resetPagesStore,
      queryClient,
      currentAccount,
      navigate
    ]
  );

  const signOut = useCallback(
    (options: SignOutOptions = {}): Promise<void> => {
      if (options.skipRevoke) {
        return Promise.resolve(navigateToLanding(options));
      } else {
        return api({
          url: "oauth/revoke",
          method: "post",
          skipVersioning: true,
          data: {
            clientId: window.PF.config.client_id,
            token: getAccessToken()
          }
        }).finally(() => navigateToLanding(options));
      }
    },
    [getAccessToken, navigateToLanding]
  );

  return { signOut };
};
